import { Box, Flex, Text } from "@chakra-ui/react";
import type { BoxProps } from "@chakra-ui/react";
import { Button } from "@/components/ui/button";
import {
    MenuContent,
    MenuRadioItem,
    MenuRadioItemGroup,
    MenuRoot,
    MenuTrigger,
} from "@/components/ui/menu";
import { useI18n } from 'src/i18n/I18nContext';
import { Language } from 'src/i18n/messages';

interface LanguageOption {
    label: string;
    value: Language;
    code: string;
}

const LanguageSelect = (props: BoxProps) => {
    const { language, setLanguage, t } = useI18n();

    const options: LanguageOption[] = [
        {
            value: 'en',
            code: 'EN',
            label: t('common.language.english'),
        },
        {
            value: 'de',
            code: 'DE',
            label: t('common.language.german'),
        },
    ];

    const selected = options.find((option) => option.value === language);

    return (
        <Box {...props}>
            <MenuRoot lazyMount positioning={{ placement: 'bottom-end', gutter: 8 }}>
                <MenuTrigger asChild>
                    <Button
                        aria-label={t('common.language.label')}
                        variant='ghost'
                        boxSize={{ base: '10', lg: '11' }}
                        minW='10'
                        maxW={{ base: '10', lg: '11' }}
                        height={{ base: '10', lg: '11' }}
                        width='full'
                        rounded={{ base: 'lg', lg: 'full' }}
                        px={{ base: '0', lg: '3' }}
                        gap='2'
                        borderWidth='1px'
                        boxShadow='md'
                        borderColor='border.subtle'
                        color='fg'
                        bg='bg.panel'
                        _hover={{ bg: 'bg.subtle' }}
                    >
                        <Text fontWeight='semibold'>{selected?.code ?? language.toUpperCase()}</Text>
                    </Button>
                </MenuTrigger>
                <MenuContent
                    minW='10rem'
                    p='2'
                    rounded='xl'
                    borderColor='border.subtle'
                    bg='bg.panel'
                    shadow='2xl'
                >
                    <MenuRadioItemGroup
                        value={language}
                        onValueChange={(details) => setLanguage(details.value as Language)}
                    >
                        {options.map((option) => (
                            <MenuRadioItem
                                key={option.value}
                                value={option.value}
                                rounded='lg'
                                ps='8'
                                pe='2.5'
                                py='2'
                                gap='2'
                            >
                                <Flex align='center' gap='1'>
                                    <Text fontWeight='semibold'>{option.code}</Text>
                                    <Text>-</Text>
                                    <Text>{option.label}</Text>
                                </Flex>
                            </MenuRadioItem>
                        ))}
                    </MenuRadioItemGroup>
                </MenuContent>
            </MenuRoot>
        </Box>
    );
};

export default LanguageSelect;
